import { Moment } from 'moment'
import React, {
  FormEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import styled from 'styled-components'
import { useIntl } from 'react-intl'

import {
  createTripEvent,
  createTripSaveEvent,
} from 'analytics/eventDefinitions'
import { fireInteractionEvent } from 'api/googleTagManager'
import TextInput from 'components/Common/Form/Input/TextInput'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import InputText from 'LuxKit/Typography/InputText'
import noop from 'lib/function/noop'
import { CreateTripError } from 'tripPlanner/api/types'
import { MAX_TRIP_NAME_CHARS } from 'tripPlanner/config'
import { useCreateTrip, useEditableTrips } from 'tripPlanner/hooks/api/trip'
import { FullTrip } from 'tripPlanner/types/common'
import { getDefaultTripImageId } from 'tripPlanner/utils'

interface Props {
  startDate?: Moment
  endDate?: Moment
  onCreateTrip: (trip: FullTrip) => void
  onCancel: () => void
}

const CharCount = styled(InputText)`
  position: absolute;
  bottom: -4px;
  transform: translateY(100%);
  right: 0px;
`

function CreateTripStart({
  startDate,
  endDate,
  onCreateTrip,
  onCancel: onCancelProp,
}: Props) {
  const intl = useIntl()
  const inputRef = useRef<HTMLInputElement>(null)

  const [name, setName] = useState('')
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined)

  const { data: trips } = useEditableTrips({ onError: noop, onSuccess: noop })

  const isFirstTrip = !!trips && trips.length === 0

  const { mutate: createTrip, isLoading } = useCreateTrip({
    onSuccess: (trip) => {
      onCreateTrip(trip)
      if (isFirstTrip) {
        fireInteractionEvent(createTripSaveEvent(trip.name, false))
      }
    },
    onError: (e: CreateTripError) => {
      setErrorMsg(e.message)
    },
  })

  const onCreate = useCallback(() => {
    if (name.length) {
      const imageId = getDefaultTripImageId(trips)

      createTrip({
        name,
        startDate,
        endDate,
        imageId,
      })
    }
  }, [endDate, trips, createTrip, name, startDate])

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }, [])

  const setEmptyTripNameError = useCallback(() => {
    setErrorMsg(
      intl.formatMessage({
        id: 'Rlhq4qvHJW',
        defaultMessage: 'Trip name cannot be empty',
        description: 'Error message shown when trip name is empty',
      }),
    )
  }, [intl])

  const createTripPlan = useCallback(
    (e: FormEvent) => {
      e.preventDefault()
      if (!isLoading) {
        if (name.length > 0) {
          onCreate()
        } else {
          setEmptyTripNameError()
        }
      }
    },
    [isLoading, name.length, onCreate, setEmptyTripNameError],
  )

  const onChangeName = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const name = e.target.value
      setName(name)
      if (name.length === 0) {
        setEmptyTripNameError()
      } else if (errorMsg) {
        setErrorMsg('')
      }
    },
    [errorMsg, setEmptyTripNameError],
  )

  const onCancel = useCallback(() => {
    if (isFirstTrip) {
      fireInteractionEvent(
        createTripEvent(
          'modal_first_trip_name',
          'global',
          'dismiss',
          true,
          false,
        ),
      )
    }
    onCancelProp()
  }, [isFirstTrip, onCancelProp])

  const modalTitle = intl.formatMessage({
    id: 'R+uVSrSK1K',
    defaultMessage: 'Name your new trip',
    description: 'Title for the create trip modal',
  })

  const tripNamePlaceholder = intl.formatMessage({
    id: 'TAhrjOrPZb',
    defaultMessage: 'e.g. Bali Trip',
    description: 'Placeholder text for trip name input field',
  })

  const tripNameLabel = intl.formatMessage({
    id: 's10/jO0rhA',
    defaultMessage: 'Trip name',
    description: 'Label for trip name input field',
  })

  const createTripButtonText = intl.formatMessage({
    id: 'dk8YYtZ7pq',
    defaultMessage: 'Create trip',
    description: 'Button text to create a new trip',
  })

  const cancelButtonText = intl.formatMessage({
    id: '12LXwHGJrp',
    defaultMessage: 'Cancel',
    description: 'Button text to cancel trip creation',
  })

  return (
    <form onSubmit={createTripPlan}>
      <ModalHeader
        title={modalTitle}
        onCloseButtonClick={onCancel}
      />
      <ModalBody>
        <ModalContent>
          <TextInput
            placeholder={tripNamePlaceholder}
            label={tripNameLabel}
            ref={inputRef}
            width="100%"
            onChange={onChangeName}
            invalidErrorMessage={errorMsg}
            manualError
            maxLength={MAX_TRIP_NAME_CHARS}
            data-testid="trip-popup-trip-input"
            required
          >
            <CharCount variant="caption" colour="neutral-three">
              {name.length}/{MAX_TRIP_NAME_CHARS}
            </CharCount>
          </TextInput>
        </ModalContent>
      </ModalBody>
      <ModalFooter
        primaryActionProps={{
          type: 'submit',
          children: createTripButtonText,
          disabled: isLoading,
        }}
        secondaryActionProps={{
          children: cancelButtonText,
          disabled: isLoading,
          onClick: onCancel,
        }}
      />
    </form>
  )
}

export default CreateTripStart
